import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSEO } from "../hooks/useSEO";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActive(0);
      setColor("");
      setSize("");
    });
  }, [id]);

  useSEO(
    product
      ? {
          title: `${product.name} — Affordable ${product.category} | Nesta`,
          description:
            (product.description || "").trim().slice(0, 155) ||
            `Buy the ${product.name} at Nesta — affordable, high quality ${(product.category || "furniture").toLowerCase()} with free UK delivery.`,
          image: product.image,
        }
      : {}
  );

  if (!product)
    return <div className="min-h-[60vh] flex items-center justify-center text-[#999] tracking-luxe uppercase text-sm">Loading…</div>;

  const gallery = product.images && product.images.length ? product.images : [product.image];
  const colorImages = product.color_images || {};
  const activeGallery =
    color && colorImages[color] && colorImages[color].length ? colorImages[color] : gallery;
  const safeActive = Math.min(active, activeGallery.length - 1);
  const sizeLabel = product.size_label || "Size";
  const sizePrices = product.size_prices || {};
  const hasSizePricing = Object.keys(sizePrices).length > 0;
  const unitPrice = size && sizePrices[size] != null ? sizePrices[size] : product.price;
  const minPrice = hasSizePricing ? Math.min(product.price, ...Object.values(sizePrices)) : product.price;

  const add = () => {
    if (product.colors?.length && !color) {
      toast.error("Please select a colour");
      return;
    }
    if (product.sizes?.length && !size) {
      toast.error(`Please select a ${sizeLabel.toLowerCase()}`);
      return;
    }
    addItem(product, qty, { color, size, size_label: sizeLabel, price: unitPrice, image: activeGallery[0] });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-black mb-10">
        <ArrowLeft size={16} /> Back to shop
      </Link>

      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <motion.div
            key={`${color}-${safeActive}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="aspect-[4/5] overflow-hidden bg-[#f5f5f0]"
          >
            <img
              data-testid="product-main-image"
              src={activeGallery[safeActive]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </motion.div>
          {activeGallery.length > 1 && (
            <div data-testid="product-thumbnails" className="flex gap-3 mt-4 flex-wrap">
              {activeGallery.map((src, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`w-20 h-24 overflow-hidden bg-[#f5f5f0] transition-opacity ${
                    safeActive === i ? "ring-2 ring-[#1a1a1a]" : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:pt-8">
          <div className="text-sm uppercase tracking-luxe text-[#999] mb-4">
            {product.category}{product.subcategory ? ` · ${product.subcategory}` : ""}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-5">{product.name}</h1>
          <div className="text-2xl mb-8" data-testid="product-price">
            {hasSizePricing && !size ? `from ${money(minPrice)}` : money(unitPrice)}
          </div>
          <p className="text-[#666] leading-relaxed mb-10 max-w-lg">{product.description}</p>

          <div className="grid grid-cols-2 gap-y-4 gap-x-8 border-t border-b border-[#e5e5e5] py-6 mb-10 text-sm">
            {product.material && (
              <div>
                <div className="text-[#999] uppercase tracking-luxe text-[11px] mb-1">Material</div>
                <div>{product.material}</div>
              </div>
            )}
            {product.dimensions && (
              <div>
                <div className="text-[#999] uppercase tracking-luxe text-[11px] mb-1">Dimensions</div>
                <div>{product.dimensions}</div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            {product.colors?.length > 0 && (
              <div className="flex-1">
                <label className="text-[11px] uppercase tracking-luxe text-[#999] mb-2 block">Colour</label>
                <select
                  data-testid="select-color"
                  value={color}
                  onChange={(e) => {
                    setColor(e.target.value);
                    setActive(0);
                  }}
                  className="w-full border border-[#1a1a1a] bg-white px-3 py-3 text-sm outline-none"
                >
                  <option value="">Select colour</option>
                  {product.colors.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}
            {product.sizes?.length > 0 && (
              <div className="flex-1">
                <label className="text-[11px] uppercase tracking-luxe text-[#999] mb-2 block">{sizeLabel}</label>
                <select
                  data-testid="select-size"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  className="w-full border border-[#1a1a1a] bg-white px-3 py-3 text-sm outline-none"
                >
                  <option value="">Select {sizeLabel.toLowerCase()}</option>
                  {product.sizes.map((s) => (
                    <option key={s} value={s}>
                      {s}{sizePrices[s] != null ? ` — ${money(sizePrices[s])}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center border border-[#1a1a1a]">
              <button
                data-testid="qty-decrease"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="p-3 hover:bg-[#f5f5f0]"
              >
                <Minus size={16} />
              </button>
              <span data-testid="qty-value" className="w-12 text-center">{qty}</span>
              <button
                data-testid="qty-increase"
                onClick={() => setQty((q) => q + 1)}
                className="p-3 hover:bg-[#f5f5f0]"
              >
                <Plus size={16} />
              </button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={add}
              className="flex-1 flex items-center justify-center gap-3 bg-[#1a1a1a] text-white px-8 py-4 text-sm uppercase tracking-luxe hover:bg-[#333] transition-colors"
            >
              <ShoppingBag size={16} /> Add to Cart
            </button>
          </div>

          <p data-testid="returns-fine-print" className="text-[11px] text-[#999] leading-relaxed mt-6">
            Free UK delivery. Please note: returns are accepted within 30 days, but customers are
            responsible for paying the cost of collection of returned items.
          </p>
        </div>
      </div>
    </div>
  );
}
